import {
  faCompressAlt,
  faWandMagicSparkles
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, TextField } from '@mui/material';
import { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type { JsonInputProps } from './JsonInput.types';

// Ref is fixing React error message in console
const JsonInput = forwardRef(({ rows, name }: JsonInputProps, _ref) => {
  const {
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors }
  } = useFormContext();

  // JSON validation function
  const validateJson = (value: string) => {
    if (value === undefined || value === '') return true; // Allow empty input (non-mandatory)
    try {
      JSON.parse(value); // Try to parse the JSON
      return true; // If parsing is successful, return true (valid)
    } catch (_e) {
      return 'Invalid JSON format.'; // Return error message if invalid
    }
  };

  // Beautify JSON
  const handleBeautifyJson = (controlName: string) => {
    try {
      const json = JSON.parse(getValues(controlName));
      setValue(controlName, JSON.stringify(json, null, 2)); // Beautify
      clearErrors(controlName);
    } catch (_e) {
      setError(controlName, {
        type: 'manual',
        message: 'Invalid JSON format'
      });
    }
  };

  // Minify JSON
  const handleMinifyJson = (controlName: string) => {
    try {
      const json = JSON.parse(getValues(controlName));
      setValue(controlName, JSON.stringify(json)); // Minify
      clearErrors(controlName);
    } catch (_e) {
      setError(controlName, {
        type: 'manual',
        message: 'Invalid JSON format'
      });
    }
  };
  return (
    <>
      <Controller
        name={name}
        control={control}
        rules={{ validate: validateJson }}
        render={({ field }) => (
          <TextField
            {...field}
            error={Boolean(errors[name])}
            helperText={errors[name]?.message?.toString() || ''}
            multiline
            rows={rows}
            fullWidth
            variant="outlined"
            sx={{
              '& div.MuiInputBase-formControl': {
                padding: '0.5rem!important',
                paddingRight: '0.75rem!important',
                paddingLeft: '0.75rem!important'
              },
              '& textarea': {
                padding: '0!important'
              }
            }}
          />
        )}
      />

      <Box
        style={{
          position: 'absolute',
          bottom: errors[name] ? '1.625rem' : '.25rem',
          right: '16px',
          display: 'flex',
          gap: '5px'
        }}
      >
        <IconButton
          onClick={() => handleBeautifyJson(name)}
          title="Beautify"
          style={{ fontSize: '12px' }}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} />
        </IconButton>
        <IconButton
          onClick={() => handleMinifyJson(name)}
          title="Minify"
          style={{ fontSize: '12px' }}
        >
          <FontAwesomeIcon icon={faCompressAlt} />
        </IconButton>
      </Box>
    </>
  );
});

JsonInput.displayName = 'JsonInput';

export default JsonInput;
